import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  Signal,
  signal,
  WritableSignal,
} from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import {
  ButtonComponent,
  IconButtonComponent,
  IPaginationEvent,
  ModalService,
  PaginationComponent,
  SvgIconComponent,
  TableComponent,
} from '@ui-lib/components';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { AsyncPipe, DatePipe } from '@angular/common';
import {
  FirmwareAction,
  IFirmwareItem,
  IFirmwareModalData,
  IFirmwaresFilterFormGroup,
} from './firmwares.model';
import { getFirmwaresTableColumnsConfig } from './firmwares-table-columns.config';
import { FirmwaresService } from './services/firmwares.service';
import { debounceTime, filter, take } from 'rxjs';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FirmwareModalComponent } from './components/firmware-modal/firmware-modal.component';
import { EquipmentModelSelectorComponent } from '../../shared/components/equipment-model-selector/equipment-model-selector.component';
import { IDictionaryItem } from '../../shared/types/dictionary-item.interface';
import { LoadingState } from '../../shared/types/loading.type';
import { PermissionService } from '../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../core/models/user-permissions.enum';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';
import { PageToolbarAction } from '../../shared/components/page-toolbar/page-toolbar.model';

@Component({
  standalone: true,
  selector: 'mon-firmwares',
  templateUrl: './firmwares.component.html',
  styleUrls: ['./firmwares.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [FirmwaresService],
  imports: [
    TranslateModule,
    IconButtonComponent,
    ReactiveFormsModule,
    ButtonComponent,
    AsyncPipe,
    TableComponent,
    PaginationComponent,
    DatePipe,
    SvgIconComponent,
    FormsModule,
    EquipmentModelSelectorComponent,
    PageToolbarComponent,
  ],
})
export class FirmwaresComponent implements OnInit {
  private readonly firmwaresService = inject(FirmwaresService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly modalService = inject(ModalService);
  private readonly permissionService = inject(PermissionService);

  protected readonly FirmwareAction = FirmwareAction;
  protected readonly LoadingState = LoadingState;
  protected readonly canManageFirmware = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.FirmwareManage),
    { initialValue: false },
  );
  protected readonly canReadEquipmentModels = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.EquipmentModelRead),
    { initialValue: false },
  );
  protected get canEditFirmwareModels(): boolean {
    return this.canManageFirmware() && this.canReadEquipmentModels();
  }

  public readonly filterForm: FormGroup<IFirmwaresFilterFormGroup> = new FormGroup({
    search: new FormControl<string>('', { nonNullable: true }),
    models: new FormControl<string[]>([], { nonNullable: true }),
  });

  protected readonly currentPage: WritableSignal<number> = signal(1);

  protected readonly isDownloadingFirmware = signal<boolean>(false);

  protected readonly firmwares$ = this.firmwaresService.firmwares$;

  protected readonly asFirmware = (item: unknown): IFirmwareItem => item as IFirmwareItem;

  public readonly firmwareLoadingState: Signal<LoadingState> =
    this.firmwaresService.firmwareLoadingState;

  public readonly totalFirmwareCount: Signal<number> = this.firmwaresService.totalFirmwareCount;

  public readonly columns = getFirmwaresTableColumnsConfig();

  protected selectedModels: IDictionaryItem<string>[] = [];

  public get searchControl(): FormControl<string> {
    return this.filterForm.get<'search'>('search') as FormControl;
  }

  protected get toolbarActions(): PageToolbarAction[] {
    return this.canEditFirmwareModels
      ? [
          {
            id: 'create',
            icon: 'plus',
            titleTranslateKey: 'monitoring.firmware.dashboard.add-firmware',
          },
        ]
      : [];
  }

  protected onToolbarAction(actionId: string): void {
    if (actionId === 'create') {
      this.onFirmwareAction(FirmwareAction.Create);
    }
  }

  public get isFilterActive(): boolean {
    const { search, models } = this.filterForm.value;
    return !!search || (this.canReadEquipmentModels() && (models?.length ?? 0) > 0);
  }

  public get selectedFiltersCount(): number {
    return this.selectedModels.length;
  }

  public getControl<T>(controlName: string): FormControl<T> {
    return this.filterForm.get(controlName) as FormControl<T>;
  }

  public ngOnInit(): void {
    this.filterForm.valueChanges
      .pipe(debounceTime(300), takeUntilDestroyed(this.destroyRef))
      .subscribe((update): void => {
        // Триггерим загрузку
        this.firmwaresService.triggerFirmwareUpdate({
          skip: 0,
          keyword: update.search,
          equipmentModelIds: this.canReadEquipmentModels() ? update.models : [],
        });

        // Сбрасываем текущую страницу
        this.currentPage.set(1);
      });
  }

  public deselectModel(model: IDictionaryItem<string>): void {
    this.getControl<string[]>('models').setValue(
      this.getControl<string[]>('models').value.filter((id: string): boolean => id !== model.id),
    );
  }

  public deselectAllModels(): void {
    this.getControl<string[]>('models').setValue([]);
  }

  public clearAllFilters(): void {
    this.searchControl.setValue('', { emitEvent: false });
    this.deselectAllModels();
  }

  public triggerFormValueChanges(): void {
    this.filterForm.updateValueAndValidity();
  }

  public onDownload(firmware: IFirmwareItem, event: MouseEvent): void {
    event.stopPropagation();

    if (this.isDownloadingFirmware()) return;

    this.isDownloadingFirmware.set(true);

    this.firmwaresService
      .downloadFirmware(firmware.id, firmware.name, firmware.originFileName)
      .subscribe({
        complete: () => this.isDownloadingFirmware.set(false),
      });
  }

  public onFirmwareAction(action: FirmwareAction, firmwareId?: string): void {
    if (!this.canEditFirmwareModels) return;

    let title: string;

    switch (action) {
      case FirmwareAction.Create:
        title = 'monitoring.firmware.modal.add-firmware';
        break;
      case FirmwareAction.Edit:
        title = 'monitoring.firmware.modal.edit-firmware';
        break;
      default:
        throw new Error('Unsupported action');
    }

    const modalRef = this.modalService.open<IFirmwareModalData>({
      component: FirmwareModalComponent,
      data: {
        title,
        action,
        firmwareId,
      },
      customContainerClass: 'firmware-modal',
    });

    modalRef.afterClose$
      .pipe(
        take(1),
        takeUntilDestroyed(this.destroyRef),
        filter((result) => !!result),
      )
      .subscribe(() => {
        this.triggerFormValueChanges();
      });
  }

  public onPageChange(event: IPaginationEvent): void {
    this.currentPage.set(event.pageIndex);
    this.firmwaresService.triggerFirmwareUpdate({
      skip: (event.pageIndex - 1) * event.pageSize,
      take: event.pageSize,
    });
  }

  public showSelectedModels(options: IDictionaryItem<string>[]): void {
    this.selectedModels = [...options];
  }
}
